import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRecommendations, updateRecommendationStatus } from "../redux/slices/recommendationsSlice";
import { MessageSquare, CheckCircle, Clock, RefreshCw, Filter } from "lucide-react";
import Swal from "sweetalert2";

const RecommendationsPanel = () => {
  const dispatch = useDispatch();
  const { recommendations, loading, error } = useSelector((state) => state.recommendations || { recommendations: [], loading: false, error: null });
  const [statusFilter, setStatusFilter] = useState("all");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    dispatch(fetchRecommendations());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchRecommendations());
  };

  const handleStatusChange = async (id, newStatus) => {
    setIsUpdating(true);
    try {
      await dispatch(updateRecommendationStatus({ id, status: newStatus }));
      Swal.fire({
        title: "¡Estado actualizado!",
        text: `La recomendación ha sido marcada como "${getStatusText(newStatus)}"`,
        icon: "success",
        background: "#1e293b",
        color: "#f8f9f9",
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "No se pudo actualizar el estado de la recomendación",
        icon: "error",
        background: "#1e293b",
        color: "#f8f9f9",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Pendiente";
      case "reviewed":
        return "Revisada";
      case "implemented":
        return "Implementada";
      default:
        return status;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="px-3 py-1 bg-yellow-800/30 text-yellow-300 rounded-full text-xs font-semibold flex items-center gap-1">
            <Clock size={12} />
            Pendiente
          </span>
        );
      case "reviewed":
        return (
          <span className="px-3 py-1 bg-blue-800/30 text-blue-300 rounded-full text-xs font-semibold flex items-center gap-1">
            <CheckCircle size={12} />
            Revisada
          </span>
        );
      case "implemented":
        return (
          <span className="px-3 py-1 bg-green-800/30 text-green-300 rounded-full text-xs font-semibold flex items-center gap-1">
            <CheckCircle size={12} />
            Implementada
          </span>
        );
      default:
        return null;
    }
  };

  const filteredRecommendations = recommendations.filter((rec) => {
    if (statusFilter === "all") return true;
    return rec.status === statusFilter;
  });

  if (loading && recommendations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 h-64">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-400">Cargando recomendaciones...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-6 text-center">
        <h3 className="text-red-300 text-xl mb-2">Error al cargar las recomendaciones</h3>
        <p className="text-red-200/70">{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-white flex items-center mx-auto gap-2"
        >
          <RefreshCw size={16} /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6 shadow-xl">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-100 flex items-center gap-2">
          <MessageSquare className="text-blue-400" />
          Recomendaciones y Feedback de Usuarios
        </h2>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-gray-700/50 rounded-lg p-1">
            <Filter size={16} className="text-gray-400 ml-2" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent text-gray-200 py-2 pr-3 pl-1 rounded-lg focus:outline-none"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="reviewed">Revisadas</option>
              <option value="implemented">Implementadas</option>
            </select>
          </div>
          
          <button
            onClick={handleRefresh}
            className="bg-blue-600 hover:bg-blue-700 p-2 rounded-lg text-white flex items-center gap-2 transition-all duration-300"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            {loading ? "Actualizando..." : "Actualizar"}
          </button>
        </div>
      </div>

      {recommendations.length === 0 ? (
        <div className="text-center py-16 bg-gray-800/30 rounded-xl border border-gray-700/50">
          <MessageSquare size={48} className="mx-auto text-gray-500 mb-4" />
          <h3 className="text-xl text-gray-300 mb-1">No hay recomendaciones todavía</h3>
          <p className="text-gray-500">
            Las recomendaciones de los usuarios aparecerán aquí una vez que sean enviadas.
          </p>
        </div>
      ) : filteredRecommendations.length === 0 ? (
        <div className="text-center py-16 bg-gray-800/30 rounded-xl border border-gray-700/50">
          <Filter size={48} className="mx-auto text-gray-500 mb-4" />
          <h3 className="text-xl text-gray-300 mb-1">No hay resultados para el filtro seleccionado</h3>
          <p className="text-gray-500">
            Intenta cambiar el filtro para ver más recomendaciones.
          </p>
        </div>
      ) : (
        <div className="grid gap-6">
          {filteredRecommendations.map((rec) => (
            <div
              key={rec.id}
              className="bg-gray-800/70 backdrop-blur-sm rounded-xl border border-gray-700/50 p-5 shadow-lg hover:shadow-blue-900/20 transition-all duration-300"
            >
              <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-900/30 p-2.5 rounded-lg">
                    <MessageSquare size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-200">
                      {rec.contentType === "peliculas" && "Películas"}
                      {rec.contentType === "series" && "Series"}
                      {rec.contentType === "anime" && "Anime"}
                      {rec.contentType === "documentales" && "Documentales"}
                      {rec.contentType === "otros" && "Otros contenidos"}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {new Date(rec.createdAt).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
                {getStatusBadge(rec.status)}
              </div>

              <div className="mb-4">
                <h4 className="text-gray-300 font-medium mb-1">Contenido favorito:</h4>
                <p className="text-gray-400 pl-2 border-l-2 border-blue-500">{rec.favoriteContent}</p>
              </div>

              <div className="mb-5">
                <h4 className="text-gray-300 font-medium mb-1">Sugerencias:</h4>
                <p className="text-gray-400 bg-gray-900/50 p-3 rounded-lg">{rec.suggestions}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-gray-700 pt-4">
                <p className="text-gray-500 text-sm mr-2">Cambiar estado:</p>
                <button
                  onClick={() => handleStatusChange(rec.id, "pending")}
                  disabled={rec.status === "pending" || isUpdating}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 ${
                    rec.status === "pending"
                      ? "bg-yellow-800/20 text-yellow-300/50 cursor-not-allowed"
                      : "bg-yellow-800/40 text-yellow-300 hover:bg-yellow-800/60"
                  }`}
                >
                  Pendiente
                </button>
                <button
                  onClick={() => handleStatusChange(rec.id, "reviewed")}
                  disabled={rec.status === "reviewed" || isUpdating}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 ${
                    rec.status === "reviewed"
                      ? "bg-blue-800/20 text-blue-300/50 cursor-not-allowed"
                      : "bg-blue-800/40 text-blue-300 hover:bg-blue-800/60"
                  }`}
                >
                  Revisada
                </button>
                <button
                  onClick={() => handleStatusChange(rec.id, "implemented")}
                  disabled={rec.status === "implemented" || isUpdating}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 ${
                    rec.status === "implemented"
                      ? "bg-green-800/20 text-green-300/50 cursor-not-allowed"
                      : "bg-green-800/40 text-green-300 hover:bg-green-800/60"
                  }`}
                >
                  Implementada
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecommendationsPanel;
